import React, { useState } from 'react';
import { Box } from '@mui/material';

interface Props {
  src: string | null;
  onClick?: () => void;
}

const SIZE = 40;

// The identity thumbnail shared by the ad and asset tables.
//
// `src` is normally the API's image proxy, which resizes to this cell and sets a
// real Cache-Control — the point being that a rendered thumbnail costs nothing to
// render again. Before that existed the grid pointed straight at Meta's CDN, which
// answers `no-store` and re-signs a different URL per request, so every scroll
// re-downloaded the full-resolution asset (~200 KB a cell) and the ones that lost
// the race went blank. See creativeImage/creativeImageLink.js in sbly-dash-collector.
//
// No `loading="lazy"`: the grid already virtualises, so only the visible rows
// mount, and deferring a 2 KB image behind a heuristic that misjudges an
// absolutely-positioned row inside a very tall scroller only adds a way to fail.
//
// Failures are tracked per URL rather than as a plain boolean because the grid
// recycles cells as it virtualises — a sticky flag would blank out the next row's
// perfectly good image.
//
// And it takes two failures, not one. Scrolling unmounts an <img> whose request is
// still in flight, which fires `error` exactly as a dead link does, so one failure
// is not evidence the image is missing — treating it as such is what left rows
// permanently empty. The retry carries a cache-buster so the browser can't serve
// the failure back from its own cache.
export default function CreativeThumbnail({ src, onClick }: Props) {
  const [failure, setFailure] = useState<{ src: string; attempts: number } | null>(
    null,
  );

  const attempts = failure && failure.src === src ? failure.attempts : 0;
  const dead = attempts >= 2;

  if (!src || dead) {
    return (
      <Box
        sx={{
          width: SIZE,
          height: SIZE,
          flexShrink: 0,
          borderRadius: 1,
          border: '1px dashed #E2E8F0',
          backgroundColor: '#F8FAFC',
        }}
      />
    );
  }

  const url = attempts === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}retry=${attempts}`;

  return (
    <Box
      component="img"
      key={url}
      src={url}
      alt=""
      onError={() => setFailure({ src, attempts: attempts + 1 })}
      onClick={onClick}
      sx={{
        width: SIZE,
        height: SIZE,
        flexShrink: 0,
        borderRadius: 1,
        objectFit: 'cover',
        border: '1px solid #E2E8F0',
        backgroundColor: '#F8FAFC',
        cursor: onClick ? 'pointer' : 'default',
      }}
    />
  );
}
